import { Box, Flex, Spinner, Tag, Text } from '@chakra-ui/react';
import { fadeInBottomAnimation } from '@texas/resources/animations/animations';
import { EntityItem } from './types';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { hoverCards } from './renders/hoverCards/hoverCards';
import { HoverCard } from '../shared/HoverCard';

interface Props {
  values: Record<string, { items: EntityItem[]; previousCount: number }>;
  totalEntities: number;
  loading: boolean;
  index: number;
  show: boolean;
  showHoverCard?: boolean;
  onClick: (item: EntityItem) => void;
}

export function SlatePopover({
  values,
  totalEntities,
  index,
  loading,
  onClick,
  showHoverCard,
  show,
}: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      const offset = ref.current.clientHeight / 2;
      ref.current.scrollTop = index * 28 - offset;
    }
  }, [index]);

  if (!show) {
    return null;
  }

  return (
    <Box
      padding="3px"
      position="relative"
      zIndex={10}
      background="texas.bg.blueTint.700"
      _light={{ background: 'texas.bg.blueTint.100' }}
      borderRadius="4px"
      boxShadow="lg"
      maxH="200px"
      ref={ref}
      overflowY="auto"
      animation={fadeInBottomAnimation()}
    >
      {loading && <Spinner />}
      {totalEntities === 0 && <Text>{t('general.noResult')}</Text>}
      {Object.entries(values).map(([category, group], i) => (
        <Box key={i}>
          <Text
            fontWeight="bold"
            fontSize="xs"
            opacity={0.4}
            px={1}
            textTransform="capitalize"
          >
            {category}
          </Text>
          {group.items.map((item, itemIndex) => (
            <HoverCard
              content={showHoverCard ? hoverCards[item.type]?.(item.id) : null}
              isOpen={itemIndex + group.previousCount === index}
              key={item.id}
            >
              <Flex
                gap={2}
                key={item.id}
                alignItems="center"
                w="full"
                pl={1}
                py={0}
                fontSize="sm"
                borderRadius="sm"
                cursor="pointer"
                onClick={() => onClick(item)}
                background={
                  itemIndex + group.previousCount === index
                    ? 'whiteAlpha.300'
                    : 'transparent'
                }
                _light={{
                  background:
                    itemIndex + group.previousCount === index
                      ? 'blackAlpha.300'
                      : 'transparent',
                }}
              >
                <Text flexGrow={1}>{item.name}</Text>
                {item.tag && <Tag>{item.tag}</Tag>}
              </Flex>
            </HoverCard>
          ))}
        </Box>
      ))}
    </Box>
  );
}
